import { supabase } from "./supabase";

declare global {
  interface Window {
    __pickleEmailTokenLogin?: Promise<boolean>;
  }
}

const LOG_PREFIX = "[pickle-auth]";

type TokenLoginResponse = {
  token_hash?: string;
  email?: string;
  error?: string;
};

function logTokenLogin(message: string, detail?: Record<string, unknown>) {
  if (detail !== undefined) {
    console.log(LOG_PREFIX, message, detail);
  } else {
    console.log(LOG_PREFIX, message);
  }
}

function stripManageTokenFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  if (!params.has("token") && !params.has("return_to")) return;
  params.delete("token");
  params.delete("return_to");
  const qs = params.toString();
  history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
}

/**
 * One-click login from emails: /path?token=<manage_token> → llc-token-login → verifyOtp.
 */
export function tryEmailTokenLogin(): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (!window.__pickleEmailTokenLogin) {
    window.__pickleEmailTokenLogin = exchange();
  }
  return window.__pickleEmailTokenLogin;
}

async function exchange(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  const emailToken = params.get("token");
  if (!emailToken) {
    logTokenLogin("no ?token= in URL — skipping email token login");
    return false;
  }

  logTokenLogin("manage_token detected in URL", {
    path: window.location.pathname,
    tokenPrefix: `${emailToken.slice(0, 8)}…`,
  });

  let success = false;
  let redirectAfter: string | null = null;

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.access_token) {
      logTokenLogin("already signed in — stripped ?token= without re-login", {
        email: session.user?.email ?? null,
      });
      success = true;
      return success;
    }

    const baseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!baseUrl || !anonKey) {
      logTokenLogin("missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY");
      return false;
    }

    logTokenLogin("calling llc-token-login…");
    const res = await fetch(`${String(baseUrl).replace(/\/$/, "")}/functions/v1/llc-token-login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
        apikey: anonKey,
      },
      body: JSON.stringify({ token: emailToken }),
    });
    const data = (await res.json().catch(() => ({}))) as TokenLoginResponse;
    if (!res.ok || !data?.token_hash) {
      logTokenLogin("llc-token-login failed", {
        status: res.status,
        error: data.error ?? "(no error message)",
      });
      return false;
    }

    logTokenLogin("llc-token-login ok — verifying OTP…", {
      email: data.email ?? null,
    });

    let result = await supabase.auth.verifyOtp({
      type: "magiclink",
      token_hash: data.token_hash,
    });
    if (result.error) {
      result = await supabase.auth.verifyOtp({
        type: "email",
        token_hash: data.token_hash,
      });
    }
    if (result.error) {
      logTokenLogin("verifyOtp failed", { message: result.error.message });
      return false;
    }

    const {
      data: { session: newSession },
    } = await supabase.auth.getSession();
    logTokenLogin("Supabase session created via email token", {
      email: newSession?.user?.email ?? data.email ?? null,
    });

    success = true;

    const returnTo = params.get("return_to");
    if (returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//")) {
      redirectAfter = returnTo;
    }

    return success;
  } catch (err) {
    logTokenLogin("email token login threw", {
      error: err instanceof Error ? err.message : String(err),
    });
    return false;
  } finally {
    if (redirectAfter) {
      logTokenLogin("redirecting after login", { returnTo: redirectAfter });
      window.location.replace(redirectAfter);
    } else {
      stripManageTokenFromUrl();
      logTokenLogin(
        success ? "token removed from URL after login" : "token removed from URL after failed login",
      );
    }
  }
}
